import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// Fiche détaillée par produit — activité de l'Assistant IA ZIFEK
// Montre qui a vu / ouvert / ajouté au panier ce produit via le chat IA,
// avec le nom du client, la requête et la date de chaque interaction.
// ─────────────────────────────────────────────────────────────

interface AiEventRow {
  id: number;
  client_user_id: number | null;
  client_name: string | null;
  event_type: string;
  query: string | null;
  created_at: string;
}

interface Props {
  productId: number;
  productName: string;
  merchantId: number | null;
  onClose: () => void;
}

const EVENT_META: Record<string, { icon: string; label: string; color: string }> = {
  display: { icon: 'ri-eye-line', label: 'Affiché dans le chat', color: 'text-secondary-700 bg-secondary-100' },
  click: { icon: 'ri-cursor-line', label: 'Fiche ouverte', color: 'text-primary-700 bg-primary-100' },
  cart: { icon: 'ri-shopping-cart-2-line', label: 'Ajouté au panier', color: 'text-accent-700 bg-accent-100' },
};

export default function AiProductStatsModal({ productId, productName, merchantId, onClose }: Props) {
  const [events, setEvents] = useState<AiEventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('ai_chat_product_events')
        .select('id, client_user_id, client_name, event_type, query, created_at')
        .eq('product_id', productId)
        .order('created_at', { ascending: false })
        .limit(300);
      if (merchantId != null) query = query.eq('merchant_id', merchantId);
      const { data, error: err } = await query;
      if (err) throw err;
      setEvents((data || []) as AiEventRow[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [productId, merchantId]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const metrics = useMemo(() => {
    const display = events.filter((e) => e.event_type === 'display').length;
    const click = events.filter((e) => e.event_type === 'click').length;
    const cart = events.filter((e) => e.event_type === 'cart').length;
    const clients = new Set(events.filter((e) => e.client_user_id).map((e) => String(e.client_user_id))).size;
    return { display, click, cart, clients };
  }, [events]);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative w-full max-w-lg bg-background-50 rounded-xl overflow-hidden max-h-[88vh] flex flex-col">
        {/* En-tête */}
        <div className="flex items-start justify-between gap-3 p-5 border-b border-background-200/70 flex-shrink-0">
          <div className="min-w-0">
            <h3 className="text-base font-bold font-heading text-foreground-950 truncate">{productName}</h3>
            <div className="flex items-center gap-2 mt-1">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-accent-100 text-accent-700 text-[11px] font-medium">
                <i className="ri-robot-2-line text-[10px]"></i>
                Source : chat IA ZIFEK
              </span>
              <span className="text-[11px] text-foreground-400">Qui a vu / commandé ce produit</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-background-200/70 transition-colors cursor-pointer flex-shrink-0"
            aria-label="Fermer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        {/* Métriques */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-4 border-b border-background-200/70 flex-shrink-0">
          <Metric icon="ri-eye-line" tone="text-secondary-700 bg-secondary-100" value={metrics.display} label="Vues" />
          <Metric icon="ri-cursor-line" tone="text-primary-700 bg-primary-100" value={metrics.click} label="Fiches" />
          <Metric icon="ri-shopping-cart-2-line" tone="text-accent-700 bg-accent-100" value={metrics.cart} label="Paniers" />
          <Metric icon="ri-user-search-line" tone="text-secondary-700 bg-secondary-100" value={metrics.clients} label="Clients" />
        </div>

        {/* Liste détaillée */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <i className="ri-loader-4-line animate-spin text-2xl text-accent-500"></i>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-32 text-center">
              <p className="text-sm text-red-500 mb-3">{error}</p>
              <button
                onClick={fetchEvents}
                className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                Réessayer
              </button>
            </div>
          ) : events.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-foreground-400">
              <i className="ri-robot-2-line text-3xl mb-2"></i>
              <p className="text-sm">Aucune interaction via l&apos;assistant IA sur ce produit</p>
            </div>
          ) : (
            <div className="space-y-1">
              <h4 className="text-xs font-semibold text-foreground-500 mb-2">Détail des interactions</h4>
              {events.map((e) => {
                const meta = EVENT_META[e.event_type] || EVENT_META.display;
                return (
                  <div key={e.id} className="flex items-start gap-3 py-2.5 border-b border-background-200/50 last:border-0">
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${meta.color}`}>
                      <i className={`${meta.icon} text-xs`}></i>
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-foreground-800">
                        <span className="font-semibold">{e.client_name || 'Un client'}</span>
                        <span className="text-foreground-500"> · {meta.label}</span>
                      </p>
                      {e.query && (
                        <p className="text-xs text-foreground-500 italic mt-0.5 truncate">Requête : « {e.query} »</p>
                      )}
                    </div>
                    <span className="text-[11px] text-foreground-400 whitespace-nowrap flex-shrink-0">
                      {new Date(e.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Metric({ icon, tone, value, label }: { icon: string; tone: string; value: number; label: string }) {
  return (
    <div className="bg-background-100 rounded-lg p-3">
      <div className={`w-7 h-7 rounded-lg flex items-center justify-center mb-1.5 ${tone}`}>
        <i className={`${icon} text-sm`}></i>
      </div>
      <div className="text-lg font-bold font-heading text-foreground-950">{value}</div>
      <div className="text-[11px] text-foreground-500">{label}</div>
    </div>
  );
}